import React, { FC, useState } from 'react';
import { Dialog } from '@scope/scope-one-ui/core';
import { useTranslation } from 'react-i18next';
import { useNotifications } from 'shared/hooks';
import { useToasts } from '@scope/scope-one-ui/core/Messages/context';
import { PortfolioData, PortfolioUploadModalProps, UploadType } from './PortfolioUploadModal.d';
import { PortfolioUploadFirstStep } from './UploadSteps/PortfolioUploadFirstStep';
import { uploadSteps, uploadTypes } from './constants';
import { PortfolioUploadSecondStep } from './UploadSteps/PortfolioUploadSecondStep';
import { tableRefreshReasonTypes as refreshReasons } from '../../../../shared/constants/tableRefreshReasonTypes';
import { useFetchPortfolios } from '../../GraphQL/portfolios/hooks';
import { uploadPortfolioUnified } from './services/PortfolioUploadServices';
import { OldPortfolioUploadModal } from './OldPortfolioUploadModal';
import { useFeatures } from '../../../../shared/hooks';

export const PortfolioUploadModal: FC<PortfolioUploadModalProps> = ({
  open,
  onClose,
  onUploaded,
  destinationPortfolioId = null,
}) => {
  const { t } = useTranslation();
  const { success, error } = useToasts();
  const { notifyError } = useNotifications();
  const { data: portfoliosData, loading: isPortfoliosLoading } = useFetchPortfolios(0, 200);
  const { portfolios } = portfoliosData || { total: 0, portfolios: [] };
  const { newPortfolioUploadModal } = useFeatures();
  const [selectedStep, setSelectedStep] = useState(uploadSteps.first);
  const [uploadType, setUploadType] = useState<UploadType>(null);
  const [portfolio, setPortfolio] = useState<PortfolioData | null>(
    destinationPortfolioId ? { portfolioId: destinationPortfolioId } : null,
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadToISIN, setUploadToISIN] = useState(false);

  const firstStep = (
    <PortfolioUploadFirstStep
      changeUploadType={(v: UploadType) => setUploadType(v)}
      uploadType={uploadType}
      setStep={(step) => setSelectedStep(step)}
    />
  );

  const portfoliosOptions = portfolios.map((item) => {
    return { label: item.name, value: item.id };
  });

  const secondStep = (
    <PortfolioUploadSecondStep
      uploadType={uploadType}
      portfolio={portfolio}
      isPortfoliosLoading={isPortfoliosLoading}
      portfoliosOptions={portfoliosOptions}
      onPortfolioSelect={(val) => setPortfolio(val)}
      onFileUpload={(file) => setUploadedFile(file)}
      uploadToISIN={(val) => setUploadToISIN(val)}
    />
  );

  const steps = { [uploadSteps.first]: firstStep, [uploadSteps.second]: secondStep };

  const modalTitle = () => {
    if (selectedStep !== uploadSteps.first) {
      return uploadType === uploadTypes.entities
        ? t('PORTFOLIO_UPLOAD.ENTITIES_PORTFOLIO_UPLOAD')
        : t('PORTFOLIO_UPLOAD.FUNDS_PORTFOLIO_UPLOAD');
    }
    return t('PORTFOLIO_UPLOAD.TITLE');
  };

  const handleUploadPortfolio = () => {
    if (!portfolio) return notifyError(t('PORTFOLIO_UPLOAD.SELECT_PORTFOLIO'));
    if (!uploadedFile) return notifyError(t('PORTFOLIO_UPLOAD.SELECT_FILE'));

    const payload = {
      file: uploadedFile,
      uploadEntitiesViaIsin: uploadType === uploadTypes.entities ? uploadToISIN : undefined,
      portfolioId: portfolio.portfolioId ?? undefined,
      portfolioName: portfolio.portfolioName ?? undefined,
    };

    return uploadPortfolioUnified(payload, uploadType)
      .then(() => {
        success({
          title: 'Uploading portfolio...',
          content: `You will be notified when the processing has finished`,
        });
        if (onUploaded) {
          onUploaded(true, refreshReasons.UPDATED);
        }
        handleClose();
      })
      .catch((err) => {
        err.json().then((res) =>
          error({
            title: 'Portfolio not uploaded!',
            content: `${res.detail}`,
          }),
        );
      });
  };

  const handleClose = () => {
    setUploadType(null);
    setUploadToISIN(false);
    setUploadedFile(null);
    setSelectedStep(uploadSteps.first);
    return onClose();
  };

  return (
    <>
      {newPortfolioUploadModal ? (
        <Dialog
          size="medium"
          open={open}
          onClose={() => handleClose()}
          title={modalTitle()}
          hideCancelButton={selectedStep === uploadSteps.first}
          cancelLabel={t('PORTFOLIO_UPLOAD.PREVIOUS')}
          onCancel={() => setSelectedStep(uploadSteps.first)}
          confirmLabel={t('PORTFOLIO_UPLOAD.UPLOAD_PORTFOLIO')}
          onConfirm={selectedStep !== uploadSteps.first ? () => handleUploadPortfolio() : undefined}
        >
          {steps[selectedStep]}
        </Dialog>
      ) : (
        <OldPortfolioUploadModal
          onClose={onClose}
          open={open}
          onUploaded={onUploaded}
          destinationPortfolioId={destinationPortfolioId ?? undefined}
        />
      )}
    </>
  );
};
